import { SectionCard } from "@/components/common/SectionCard";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import type { MfgRecipeGroupSummary } from "@/lib/manufacturing/recipeGroupsClient";
import { ManufacturingModelsBulkChrome } from "./ManufacturingModelsBulkChrome";
import { ManufacturingModelsGroupAuthoringChrome } from "./ManufacturingModelsGroupAuthoringChrome";
import { ManufacturingModelsRecipeList } from "./ManufacturingModelsRecipeList";
import type { ModelsActiveFilter } from "./useManufacturingModelsListView";
import type { ModelsListPageSize } from "./useManufacturingModelsListPagination";

type ListViewSlice = {
  search: string;
  setSearch: (next: string) => void;
  activeFilter: ModelsActiveFilter;
  setActiveFilter: (next: ModelsActiveFilter) => void;
  needsReviewOnly: boolean;
  setNeedsReviewOnly: (next: boolean) => void;
  recipeGroupId: string;
  setRecipeGroupId: (next: string) => void;
  recipeGroups: ReadonlyArray<MfgRecipeGroupSummary>;
  filteredCount: number;
  page: number;
  pageSize: ModelsListPageSize;
  pageCount: number;
  rangeStart: number;
  rangeEnd: number;
  pageItems: MfgRecipeSummary[];
  setPage: (next: number) => void;
  setPageSize: (next: ModelsListPageSize) => void;
  reloadRecipeGroups?: () => void;
};

type BulkSlice = {
  selectedCount: number;
  selectedRecipes: readonly MfgRecipeSummary[];
  selectedIds: Set<string>;
  allFilteredSelected: boolean;
  editOpen: boolean;
  selectAllFiltered: () => void;
  clearSelection: () => void;
  toggleSelected: (id: string) => void;
  setEditOpen: (open: boolean) => void;
};

/** Left column: bulk chrome + recipe list for Models workspace. */
export function ManufacturingModelsListColumn({
  isCreate,
  recipes,
  listView,
  bulk,
  selectedId,
  creating,
  listBusy,
  pendingCounts,
  canEditCatalogCode,
  canDeactivateModel,
  canPermanentlyDelete,
  onPick,
  onDuplicate,
  onRequestPermanentDelete,
  onBulkApplied,
  onConfirmReview,
  confirmBusy,
  onOpenBomSandbox,
  onOpenPriceChange,
  onToast,
}: {
  isCreate: boolean;
  recipes: MfgRecipeSummary[];
  listView: ListViewSlice;
  bulk: BulkSlice;
  selectedId: string | null;
  creating: boolean;
  listBusy: boolean;
  pendingCounts: Record<string, number>;
  canEditCatalogCode: boolean;
  canDeactivateModel: boolean;
  canPermanentlyDelete: boolean;
  onPick: (id: string) => void;
  onDuplicate: (id: string) => void;
  onRequestPermanentDelete: (recipe: MfgRecipeSummary) => void;
  onBulkApplied: () => void | Promise<void>;
  onConfirmReview: (recipeIds: string[]) => void;
  confirmBusy?: boolean;
  onOpenBomSandbox: (groupId?: string) => void;
  onOpenPriceChange?: () => void;
  onToast?: (message: string) => void;
}) {
  return (
    <SectionCard
      title="Build models (BOM)"
      description="Select a model to edit its bill of materials. Lines may be catalog materials or sub-recipes. Costs roll up nested SKUs to leaf materials using current WAC."
    >
      <div className="space-y-3">
        {isCreate ? (
          <ManufacturingModelsBulkChrome
            selectedCount={bulk.selectedCount}
            filteredCount={listView.filteredCount}
            allFilteredSelected={bulk.allFilteredSelected}
            selectedRecipes={bulk.selectedRecipes}
            allRecipes={recipes}
            recipeGroups={listView.recipeGroups}
            canEditCode={canEditCatalogCode}
            canArchive={canDeactivateModel}
            canPermanentlyDelete={canPermanentlyDelete}
            editOpen={bulk.editOpen}
            pendingCounts={pendingCounts}
            confirmBusy={confirmBusy}
            onSelectAllFiltered={bulk.selectAllFiltered}
            onClear={bulk.clearSelection}
            onOpenEdit={() => bulk.setEditOpen(true)}
            onCloseEdit={() => bulk.setEditOpen(false)}
            onApplied={onBulkApplied}
            onConfirmReview={onConfirmReview}
            onToast={onToast}
          />
        ) : null}
        <ManufacturingModelsGroupAuthoringChrome
          isCreate={isCreate}
          recipeGroupId={listView.recipeGroupId}
          recipeGroups={listView.recipeGroups}
          recipes={recipes}
          canEditCode={canEditCatalogCode}
          onRecipesChanged={async () => {
            listView.reloadRecipeGroups?.();
            await onBulkApplied();
          }}
          onPickRecipe={onPick}
          onOpenBomSandbox={(groupId) => onOpenBomSandbox(groupId)}
          onOpenPriceChange={onOpenPriceChange}
          onToast={onToast}
        />
        <ManufacturingModelsRecipeList
          recipes={recipes}
          pageItems={listView.pageItems}
          selectedId={selectedId}
          creating={creating}
          loading={listBusy}
          pendingCounts={pendingCounts}
          search={listView.search}
          onSearchChange={listView.setSearch}
          activeFilter={listView.activeFilter}
          onActiveFilterChange={listView.setActiveFilter}
          needsReviewOnly={listView.needsReviewOnly}
          onNeedsReviewOnlyChange={listView.setNeedsReviewOnly}
          recipeGroupId={listView.recipeGroupId}
          onRecipeGroupIdChange={listView.setRecipeGroupId}
          recipeGroups={listView.recipeGroups}
          page={listView.page}
          pageSize={listView.pageSize}
          pageCount={listView.pageCount}
          rangeStart={listView.rangeStart}
          rangeEnd={listView.rangeEnd}
          filteredCount={listView.filteredCount}
          onSetPage={listView.setPage}
          onSetPageSize={listView.setPageSize}
          onPick={onPick}
          showDuplicate={isCreate}
          canPermanentlyDelete={canPermanentlyDelete && isCreate}
          onDuplicate={onDuplicate}
          onRequestPermanentDelete={onRequestPermanentDelete}
          bulkEnabled={isCreate}
          bulkSelectedIds={bulk.selectedIds}
          onToggleBulk={bulk.toggleSelected}
          onSelectAllFiltered={bulk.selectAllFiltered}
          allFilteredSelected={bulk.allFilteredSelected}
          confirmBusy={confirmBusy}
          onConfirmReview={
            isCreate ? (id) => onConfirmReview([id]) : undefined
          }
        />
      </div>
    </SectionCard>
  );
}
